import React, { useState, useEffect, useRef, useMemo } from 'react';
import RightModal from "../components/templates/RightModal";
import { getJobsList, selectJobsData, selectJobsLoading } from "../redux/jobs/jobsSlice";
import DynamicIcon from "../components/DynamicIcon";
import ViewJob from "../components/jobsPage/ViewJob";
import LoadingSection from "../components/LoadingSection";
import { selectAccountData } from "../redux/main/accountSlice";
import { resetRefreshNow, selectRightModalId, selectRightModalIsOpen, selectRightModalIsRefreshNow, selectRightModalmode, setModal } from "../redux/rightModalSlice";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from 'react-router-dom';
import DynamicTitle from "../components/DynamicTitle";
import CreateJob from './CreateJob';
import { useColorPalette } from '../hooks/useColorPalette';
import DataTable from '../components/DataTable';
import moment from "moment";
import Select2 from '../components/Select2';
import { toast } from 'react-toastify';

export default function JobsList() {
    const dispatch = useDispatch();
    const palette = useColorPalette();
    const navigate = useNavigate();
    const jobsList = useSelector(selectJobsData);
    const jobsLoading = useSelector(selectJobsLoading);
    const accountData = useSelector(selectAccountData);
    const token = localStorage.getItem('installamelo-auth-token');
    const [searchTerm, setSearchTerm] = useState('');
    const [jobStatusFilter, setJobStatusFilter] = useState('');
    const [jobTypeFilter, setJobTypeFilter] = useState('');
    const [systemTypeFilter, setSystemTypeFilter] = useState('');

    // Stati per la modale di cambio stato
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [statusToUpdate, setStatusToUpdate] = useState(null);
    const [jobToUpdateStatus, setJobToUpdateStatus] = useState(null);
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
    
    // Stati per la modale di export Excel
    const [showExportModal, setShowExportModal] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [exportProgress, setExportProgress] = useState(0);
    
    // Stati per la modale di eliminazione
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [jobToDelete, setJobToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const searchTimeout = useRef(null);
    
    // RightModal gestita tramite Redux
    const rightModalIsOpen = useSelector(selectRightModalIsOpen);
    const rightModalMode = useSelector(selectRightModalmode);
    const rightModalJobId = useSelector(selectRightModalId);

    const jobStatusOptions = useMemo(() => {
        return accountData?.settings?.jobStatus?.map(status => ({
            id: status._id,
            text: status.label
        })) || [];
    }, [accountData?.settings?.jobStatus]);

    const systemTypeOptions = useMemo(() => {
        return accountData?.settings?.systemTypes?.map(type => ({
            id: type._id,
            text: type.label
        })) || [];
    }, [accountData?.settings?.systemTypes]);
    
    // Funzione che verrà passata al componente DataTable per gestire la paginazione
    // Ora page è l'indice di pagina (0-based) e non più l'offset
    const handlePageChange = (pageIndex, pageSize) => {
        // Calcoliamo l'offset start in base all'indice di pagina
        const start = pageIndex * pageSize;
        
        console.log('[DEBUG PAGINATION] JobsList handlePageChange - pageIndex:', pageIndex, 'start:', start);
        
        // Eseguo la chiamata per ottenere i dati con i nuovi parametri di paginazione
        dispatch(getJobsList({
            accountId: accountData?._id,
            token,
            search: searchTerm.length >= 2 ? searchTerm : '',
            start,
            length: pageSize,
            jobStatusId: jobStatusFilter,
            jobTypeId: jobTypeFilter,
            systemTypeId: systemTypeFilter
        }));
        dispatch(resetRefreshNow());
    };
    
    // Funzione per gestire la ricerca
    const handleStatusChange = (selected) => {
        setJobStatusFilter(selected ? selected.id : '');
    };

    const handleSystemTypeChange = (selected) => {
        setSystemTypeFilter(selected ? selected.id : '');
    };

    const handleSearch = (value) => {
        setSearchTerm(value);

        // Debounce della ricerca
        if (searchTimeout.current) {
            clearTimeout(searchTimeout.current);
        }

        searchTimeout.current = setTimeout(() => {
            dispatch(getJobsList({
                accountId: accountData?._id,
                token,
                search: value.length >= 2 ? value : '',
                start: 0,
                length: 25,
                jobStatusId: jobStatusFilter,
                jobTypeId: jobTypeFilter,
                systemTypeId: systemTypeFilter
            }));
            dispatch(resetRefreshNow());
        }, 300); // Esegui la ricerca dopo 300ms di inattività
    };

    // Chiamata API ogni volta che cambiano i filtri o la ricerca
    useEffect(() => {
        if (accountData?._id) {
            dispatch(getJobsList({
                accountId: accountData._id,
                token,
                search: searchTerm.length >= 2 ? searchTerm : '',
                start: 0,
                length: 25,
                jobStatusId: jobStatusFilter,
                jobTypeId: jobTypeFilter,
                systemTypeId: systemTypeFilter
            }));
            dispatch(resetRefreshNow());
        }
    }, [accountData?._id, searchTerm, jobStatusFilter, jobTypeFilter, systemTypeFilter]);

    

    
    // Reset filtro stato a nessuna selezione su cambio account o lista stati
    useEffect(() => {
        setJobStatusFilter('');
    }, [accountData?._id, accountData?.settings?.jobStatus]);

    // Opzioni per i filtri (questi dovrebbero essere recuperati dall'API in un'applicazione reale)

    const jobTypeOptions = accountData?.settings?.jobTypes || [];
    
    // Funzione per chiudere la modale di cambio stato
    const handleCloseStatusModal = () => {
        setShowStatusModal(false);
        setJobToUpdateStatus(null);
        setStatusToUpdate(null);
    };
    
    // Funzione per aggiornare lo stato della commessa
    const handleUpdateStatus = async () => {
        if (!jobToUpdateStatus || !statusToUpdate) return;
        
        try {
            setIsUpdatingStatus(true);
            
            // Trova i dettagli del nuovo stato dalla lista degli stati disponibili
            const newStatus = accountData.settings.jobStatus.find(s => s._id === statusToUpdate);
            if (!newStatus) {
                throw new Error('Stato selezionato non valido');
            }
            
            // Prepara l'oggetto commessa aggiornato con il nuovo stato
            const updatedJob = {
                ...jobToUpdateStatus,
                statusId: newStatus._id,
                statusLabel: newStatus.label
            };
            
            // Chiamata API per aggiornare lo stato
            const token = localStorage.getItem('installamelo-auth-token');
            
            // Prepara il payload con la struttura corretta attesa dall'API
            const payload = {
                job: updatedJob
            };
            
            console.log('Invio payload all\'API:', payload);
            
            const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || ''}/jobs`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'auth-token': token
                },
                body: JSON.stringify(payload)
            });
            
            if (!response.ok) {
                throw new Error(`Errore nella risposta: ${response.status}`);
            }
            
            const result = await response.json();
            console.log('Commessa aggiornata:', result);
            
            // Ricarica la lista delle commesse per aggiornare i dati
            dispatch(getJobsList({
                accountId: accountData._id,
                token,
                search: searchTerm.length >= 2 ? searchTerm : '',
                start: 0,
                length: 25,
                jobStatusId: jobStatusFilter,
                jobTypeId: jobTypeFilter,
                systemTypeId: systemTypeFilter
            }));
            
            // Chiudi la modale e mostra un messaggio di successo
            handleCloseStatusModal();
            toast.success(`Stato aggiornato a: ${newStatus.label}`);
        } catch (error) {
            console.error('Errore durante l\'aggiornamento dello stato:', error);
            toast.error(`Errore durante l'aggiornamento: ${error.message}`);
        } finally {
            setIsUpdatingStatus(false);
        }
    };

    if (!palette) {
        return <LoadingSection isFixed />;
    }

    return (
        <div className="page">
            {jobsLoading && <LoadingSection isFixed />}
            <DynamicTitle title="Commesse" />
            
            <div className="flex justify-between items-center mb-4">
                <h1 className="text-2xl font-bold">Commesse</h1>
                <div className="flex gap-2">
                    <button 
                        onClick={() => setShowExportModal(true)}
                        style={{ 
                            backgroundColor: '#10b981', 
                            color: '#fff', 
                            padding: '0.5rem 1rem', 
                            borderRadius: '0.375rem',
                            border: 'none',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s ease'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#059669'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#10b981'}
                        title="Esporta tutte le commesse in Excel"
                    >
                        <DynamicIcon iconClass="fas fa-file-excel" />
                        {' '}Export Excel
                    </button>
                    <button 
                        onClick={() => navigate('/createJob')}
                        style={{ 
                            backgroundColor: palette.primary, 
                            color: palette.textOnPrimary, 
                            padding: '0.5rem 1rem', 
                            borderRadius: '0.375rem',
                            border: 'none',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s ease'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2a2a2a'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = palette.primary}
                    >
                        <DynamicIcon iconClass="fas fa-plus" />
                        Nuova Commessa
                    </button>
                </div>
            </div>
            <div className="box">
                <div className="w-full mb-4">
                    <div className="flex flex-col md:flex-row gap-4 md:items-center">
                        {/* Campo di ricerca */}
                        <div className="relative w-full md:w-1/3">
                            <input
                                type="text"
                                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 pr-10"
                                placeholder="Cerca commessa..."
                                value={searchTerm}
                                onChange={(e) => handleSearch(e.target.value)}
                            />
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                <DynamicIcon iconClass="fa-solid fa-search" />
                            </div>
                        </div>

                        {/* Select per filtro stato */}
                        <div className="relative w-full md:w-1/3">
                            <label htmlFor="jobStatusFilter" className="sr-only">Filtra per stato</label>
                            <Select2
                                id="jobStatusFilter"
                                placeholder="Filtra per stato..."
                                allowClear={true}
                                options={jobStatusOptions}
                                value={jobStatusFilter ? (jobStatusOptions.find(opt => opt.id === jobStatusFilter) || null) : null}
                                onChange={handleStatusChange}
                                minimumInputLength={0}
                            />
                        </div>

                        {/* Select per filtro tipo impianto */}
                        <div className="relative w-full md:w-1/3">
                            <label htmlFor="systemTypeFilter" className="sr-only">Filtra per tipo impianto</label>
                            <Select2
                                id="systemTypeFilter"
                                placeholder="Filtra per tipo impianto..."
                                allowClear={true}
                                options={systemTypeOptions}
                                value={systemTypeFilter ? (systemTypeOptions.find(opt => opt.id === systemTypeFilter) || null) : null}
                                onChange={handleSystemTypeChange}
                                minimumInputLength={0}
                            />
                        </div>
                    </div>
                </div>

                {/* Definizione colonne per DataTable */}
                <DataTable 
                    columns={[
                        { id: 'code', title: 'CODICE / DATA', width: '12%' },
                        { id: 'customer', title: 'CLIENTE', width: '22%' },
                        { id: 'jobType', title: 'TIPO', width: '14%' },
                        { id: 'system', title: 'IMPIANTO', width: '25%' },
                        { id: 'status', title: 'STATO', width: '16%' },
                        { id: 'actions', title: 'AZIONI', width: '5%' }
                    ]}
                    data={jobsList?.data || []}
                    loading={jobsLoading}
                    totalRecords={jobsList?.recordsFiltered || 0}
                    pageSize={25}
                    onPageChange={handlePageChange}
                    onSearch={handleSearch}
                    emptyMessage="Nessuna commessa trovata"
                    renderCustomRow={(job, index, isEven, tableStyles) => {
                        // Formatta la data di creazione
                        const formattedDate = job.creationDate 
                            ? moment(job.creationDate).format("DD/MM/YYYY")
                            : "--/--/----";
                            
                        // Funzione per determinare lo stile dello stato utilizzando i colori da accountData.settings.jobStatus
                        const getStatusStyle = () => {
                            // Stile di base
                            const baseStyle = {
                                padding: '0.25rem 0.5rem',  // px-2 py-1 equivalent
                                borderRadius: '9999px',    // rounded-full equivalent
                                fontSize: '0.75rem',       // text-xs equivalent
                                fontWeight: 600,           // font-semibold equivalent
                                display: 'inline-block',
                                backgroundColor: 'rgba(229, 231, 235, 1)', // bg-gray-100 equivalent
                                color: 'rgba(31, 41, 55, 1)'              // text-gray-800 equivalent
                            };
                            
                            // Cerca lo stato corrispondente nella configurazione dell'account
                            const statusConfig = accountData?.settings?.jobStatus?.find(
                                status => status._id === job.statusId
                            );

                            if (statusConfig && statusConfig.color) {
                                const hexColor = statusConfig.color;
                                
                                // Applica il colore direttamente nello stile inline
                                baseStyle.backgroundColor = `${hexColor}20`; // 20 equivale a opacità 0.12
                                baseStyle.color = hexColor;
                                console.log('[DEBUG STATUS] nessun colore trovato, uso default');
                            }
                            
                            return baseStyle;
                        };
                        
                        // Debug: visualizza in console lo stile applicato
                        console.log('[DEBUG FINAL STYLE]', getStatusStyle());
                        
                        // Funzioni per gestire le azioni
                        const handleViewClick = (e) => {
                            e.stopPropagation();
                            dispatch(setModal({
                                mode: 'view',
                                id: job._id
                            }));
                        };
                        
                        const handleEditClick = (e) => {
                            e.stopPropagation();
                            navigate(`/job/edit/${job._id}`);
                        };
                        
                        // Funzione per verificare se la commessa può essere pianificata
                        const canScheduleJob = () => {
                            if (!accountData?.settings?.jobStatus || !job.statusId) return true;
                            
                            // Trova gli stati "fatturata" e "archiviata"
                            const disabledStatusCodes = ['fatturata', 'archiviata'];
                            const disabledStatusIds = accountData.settings.jobStatus
                                .filter(status => disabledStatusCodes.includes(status.code))
                                .map(status => status._id);
                            
                            // Verifica se lo stato corrente della commessa è tra quelli disabilitati
                            return !disabledStatusIds.includes(job.statusId);
                        };
                        
                        const handleScheduleClick = (e) => {
                            e.stopPropagation();
                            if (canScheduleJob()) {
                                navigate(`/jobs/scheduling?jobId=${job._id}`);
                            }
                        };
                        
                        const handleRowClick = () => {
                            dispatch(setModal({
                                mode: 'view',
                                id: job._id
                            }));
                        };
                        
                        const handleStatusChangeClick = (e) => {
                            e.stopPropagation();
                            console.log('handleStatusChangeClick chiamata con:', job);
                            
                            if (!job) {
                                console.error('Job non definita!');
                                return;
                            }
                            
                            if (!accountData?.settings?.jobStatus) {
                                console.error('Settings degli stati commessa non disponibili!');
                                return;
                            }
                            
                            console.log('statusId della commessa:', job.statusId);
                            
                            setJobToUpdateStatus(job);
                            setStatusToUpdate(job.statusId || accountData.settings.jobStatus[0]?._id);
                            setShowStatusModal(true);
                            console.log('Modale dovrebbe essere aperta ora, showStatusModal impostato a:', true);
                        };
                        
                        return (
                            <div 
                                key={job._id}
                                className="data-table-row cursor-pointer"
                                style={{
                                    backgroundColor: isEven ? tableStyles.evenRow.backgroundColor : tableStyles.oddRow.backgroundColor,
                                    color: isEven ? tableStyles.evenRow.color : tableStyles.oddRow.color,
                                }}
                                onClick={handleRowClick}
                            >
                                <div className="data-table-cell" style={{ width: '12%' }} data-label="Codice / Data">
                                    <div className="text-center">
                                        <div className="font-semibold uppercase">{job.code || "--"}</div>
                                        {job.oldSWCode && (
                                            <div className="text-xs text-blue-600 italic" title="Codice vecchio software">
                                                ({job.oldSWCode})
                                            </div>
                                        )}
                                        <div className="text-xs text-gray-500 uppercase">{formattedDate}</div>
                                    </div>
                                </div>
                                
                                <div className="data-table-cell" style={{ width: '22%' }} data-label="Cliente">
                                    <div className="text-center uppercase">
                                        {job.customerLabel || "--"}
                                    </div>
                                </div>
                                
                                <div className="data-table-cell" style={{ width: '14%' }} data-label="Tipo">
                                    <div className="text-center uppercase">
                                        {job.jobTypeLabel || "--"}
                                    </div>
                                </div>
                                
                                <div className="data-table-cell" style={{ width: '25%' }} data-label="Impianto">
                                    <div className="text-center">
                                        <div className="font-medium truncate uppercase">{job.systemLabel || "--"}</div>
                                        {/* Tipi impianto come badge */}
                                        {job.systemTypeList && job.systemTypeList.length > 0 && (
                                            <div className="flex flex-wrap justify-center gap-1 mt-1">
                                                {job.systemTypeList.map((type, idx) => (
                                                    <span 
                                                        key={idx}
                                                        className="inline-block text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-800"
                                                        style={{ fontSize: '10px' }}
                                                    >
                                                        {type.typeLabel || 'N/D'}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                        <div 
                                            className="text-xs italic text-gray-600 uppercase mt-1"
                                            title={job.systemAddressLabel || "--"}
                                        >
                                            {job.systemAddressLabel 
                                                ? (job.systemAddressLabel.length > 35 
                                                    ? job.systemAddressLabel.substring(0, 35) + '...' 
                                                    : job.systemAddressLabel)
                                                : "--"}
                                        </div>
                                        {job.geoData && (
                                            <div className="text-xs text-gray-600 mt-1 uppercase">
                                                {job.geoData.distanceKm !== undefined ? `${parseInt(job.geoData.distanceKm)} KM` : "--"} 
                                                {job.geoData.distanceTime !== undefined ? ` • ${parseInt(job.geoData.distanceTime)} MIN` : ""}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                
                                <div className="data-table-cell" style={{ width: '16%' }} data-label="Stato">
                                    <span style={getStatusStyle()} className="uppercase">
                                        {job.statusLabel || "NON DEFINITO"}
                                    </span>
                                </div>
                                
                                <div className="data-table-cell" style={{ width: '5%' }} data-label="Azioni">
                                    <div className="flex justify-center space-x-2">
                                        <button 
                                            className="p-1 hover:bg-blue-100 rounded-full"
                                            onClick={handleViewClick}
                                            title="Visualizza"
                                        >
                                            <DynamicIcon iconClass="fa-solid fa-eye" />
                                        </button>
                                        <button 
                                            className="p-1 hover:bg-blue-100 rounded-full"
                                            onClick={handleEditClick}
                                            title="Modifica"
                                        >
                                            <DynamicIcon iconClass="fa-solid fa-edit" />
                                        </button>
                                        <button 
                                            className={`p-1 rounded-full ${canScheduleJob() ? 'hover:bg-green-100' : 'cursor-not-allowed opacity-50'}`}
                                            onClick={handleScheduleClick}
                                            title={canScheduleJob() ? "Pianifica" : "Non pianificabile (commessa fatturata o archiviata)"}
                                            disabled={!canScheduleJob()}
                                        >
                                            <DynamicIcon iconClass={`fa-solid fa-calendar ${canScheduleJob() ? 'text-green-600' : 'text-gray-400'}`} />
                                        </button>
                                        <button 
                                            className="p-1 hover:bg-orange-100 rounded-full"
                                            onClick={handleStatusChangeClick}
                                            title="Cambia Stato"
                                        >
                                            <DynamicIcon iconClass="fa-solid fa-arrows-rotate text-orange-600" />
                                        </button>
                                        <button 
                                            className="p-1 hover:bg-red-100 rounded-full"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setJobToDelete(job);
                                                setShowDeleteModal(true);
                                            }}
                                            title="Elimina"
                                        >
                                            <DynamicIcon iconClass="fa-solid fa-trash text-red-600" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    }}
                />
            </div>

            {/* RIGHT MODAL solo per la visualizzazione del dettaglio commessa */}
            {
                rightModalIsOpen && (
                    <RightModal>
                        {
                            rightModalMode === 'view' && rightModalJobId && (
                                <ViewJob jobId={rightModalJobId} />
                            )
                        }
                    </RightModal>
                )
            }
            
            {/* Modale per il cambio di stato */}
            {showStatusModal && (
                <div 
                    style={{
                        display: 'block', 
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        width: '100vw',
                        height: '100vh',
                        zIndex: 9999,
                        overflow: 'auto',
                        paddingLeft: 0,
                        paddingRight: 0
                    }} 
                    tabIndex="-1"
                >
                    <div style={{
                        position: 'relative',
                        width: '500px',
                        maxWidth: '90%',
                        margin: '10vh auto',
                        backgroundColor: '#fff',
                        borderRadius: '5px',
                        boxShadow: '0 3px 9px rgba(0,0,0,.5)',
                        animation: 'fadeIn 0.3s'
                    }}>
                        <div style={{ backgroundColor: '#fff', border: 'none', borderRadius: '5px', overflow: 'hidden' }}>
                            <div style={{ padding: '15px', borderBottom: '1px solid #e5e5e5', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <h5 style={{ margin: 0, fontWeight: 'bold' }}>Cambia Stato Commessa</h5>
                                <button 
                                    type="button" 
                                    onClick={handleCloseStatusModal}
                                    style={{
                                        border: 'none',
                                        background: 'transparent',
                                        fontSize: '20px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    &times;
                                </button>
                            </div>
                            <div style={{ padding: '15px' }}>
                                {jobToUpdateStatus && (
                                    <div>
                                        <p><strong>Cliente:</strong> {jobToUpdateStatus.customerLabel || 'N/D'}</p>
                                        <p><strong>Codice:</strong> {jobToUpdateStatus.code || 'N/D'}</p>
                                        <p><strong>Tipo:</strong> {jobToUpdateStatus.jobTypeLabel || 'N/D'}</p>
                                        
                                        <div style={{ marginTop: '20px', marginBottom: '15px' }}>
                                            <label htmlFor="status-select" style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>Seleziona nuovo stato:</label>
                                            <select
                                                id="status-select"
                                                value={statusToUpdate}
                                                onChange={(e) => setStatusToUpdate(e.target.value)}
                                                disabled={isUpdatingStatus}
                                                style={{
                                                    width: '100%',
                                                    padding: '8px 12px',
                                                    border: '1px solid #ced4da',
                                                    borderRadius: '4px',
                                                    appearance: 'auto'
                                                }}
                                            >
                                                {accountData?.settings?.jobStatus?.map((status) => (
                                                    <option key={status._id} value={status._id}>
                                                        {status.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div style={{ padding: '15px', borderTop: '1px solid #e5e5e5', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                                <button 
                                    type="button" 
                                    onClick={handleCloseStatusModal}
                                    disabled={isUpdatingStatus}
                                    style={{
                                        padding: '8px 16px',
                                        backgroundColor: '#6c757d',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: isUpdatingStatus ? 'not-allowed' : 'pointer',
                                        opacity: isUpdatingStatus ? 0.65 : 1
                                    }}
                                >
                                    Annulla
                                </button>
                                <button 
                                    type="button" 
                                    onClick={handleUpdateStatus}
                                    disabled={isUpdatingStatus}
                                    style={{
                                        padding: '8px 16px',
                                        backgroundColor: palette.primary,
                                        color: palette.textOnPrimary,
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: isUpdatingStatus ? 'not-allowed' : 'pointer',
                                        opacity: isUpdatingStatus ? 0.65 : 1
                                    }}
                                >
                                    {isUpdatingStatus ? 'Aggiornamento...' : 'Aggiorna Stato'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Modale per l'export Excel */}
            {showExportModal && (
                <div 
                    style={{
                        display: 'block', 
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        width: '100vw',
                        height: '100vh',
                        zIndex: 9999,
                        overflow: 'auto',
                        paddingLeft: 0,
                        paddingRight: 0
                    }} 
                    tabIndex="-1"
                >
                    <div style={{
                        position: 'relative',
                        width: '450px',
                        maxWidth: '90%',
                        margin: '15vh auto',
                        backgroundColor: '#fff',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,.3)',
                        animation: 'fadeIn 0.3s'
                    }}>
                        <div style={{ backgroundColor: '#fff', border: 'none', borderRadius: '8px', overflow: 'hidden' }}>
                            <div style={{ padding: '20px', borderBottom: '1px solid #e5e5e5', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <h5 style={{ margin: 0, fontWeight: 'bold', fontSize: '1.1rem' }}>
                                    <DynamicIcon iconClass="fas fa-file-excel" style={{ color: '#10b981', marginRight: '10px' }} />
                                    Export Commesse
                                </h5>
                                {!isExporting && (
                                    <button 
                                        type="button" 
                                        onClick={() => setShowExportModal(false)}
                                        style={{
                                            border: 'none',
                                            background: 'transparent',
                                            fontSize: '22px',
                                            cursor: 'pointer',
                                            color: '#666'
                                        }}
                                    >
                                        &times;
                                    </button>
                                )}
                            </div>
                            <div style={{ padding: '25px' }}>
                                {!isExporting ? (
                                    <div style={{ textAlign: 'center' }}>
                                        <DynamicIcon iconClass="fas fa-file-excel" style={{ fontSize: '48px', color: '#10b981', marginBottom: '15px' }} />
                                        <p style={{ marginBottom: '10px', fontSize: '1rem' }}>
                                            Stai per esportare tutte le commesse in formato Excel.
                                        </p>
                                        <p style={{ color: '#666', fontSize: '0.9rem' }}>
                                            Il file conterrà tutti i dati delle commesse ordinati per data.
                                        </p>
                                    </div>
                                ) : (
                                    <div style={{ textAlign: 'center' }}>
                                        <DynamicIcon iconClass="fas fa-spinner fa-spin" style={{ fontSize: '32px', color: '#10b981', marginBottom: '15px' }} />
                                        <p style={{ marginBottom: '15px', fontWeight: 500 }}>Esportazione in corso...</p>
                                        
                                        {/* Progress bar */}
                                        <div style={{ 
                                            width: '100%', 
                                            backgroundColor: '#e5e7eb', 
                                            borderRadius: '9999px', 
                                            height: '12px',
                                            overflow: 'hidden'
                                        }}>
                                            <div style={{ 
                                                width: `${exportProgress}%`, 
                                                backgroundColor: '#10b981', 
                                                height: '100%',
                                                borderRadius: '9999px',
                                                transition: 'width 0.3s ease'
                                            }}></div>
                                        </div>
                                        <p style={{ marginTop: '10px', color: '#666', fontSize: '0.85rem' }}>
                                            {exportProgress < 100 ? 'Preparazione file...' : 'Download in corso...'}
                                        </p>
                                    </div>
                                )}
                            </div>
                            {!isExporting && (
                                <div style={{ padding: '15px 20px', borderTop: '1px solid #e5e5e5', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                                    <button 
                                        type="button" 
                                        onClick={() => setShowExportModal(false)}
                                        style={{
                                            padding: '10px 20px',
                                            backgroundColor: '#6c757d',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            fontWeight: 500
                                        }}
                                    >
                                        Annulla
                                    </button>
                                    <button 
                                        type="button" 
                                        onClick={async () => {
                                            setIsExporting(true);
                                            setExportProgress(0);
                                            
                                            // Simula progress iniziale
                                            const progressInterval = setInterval(() => {
                                                setExportProgress(prev => {
                                                    if (prev >= 90) {
                                                        clearInterval(progressInterval);
                                                        return 90;
                                                    }
                                                    return prev + 10;
                                                });
                                            }, 200);
                                            
                                            try {
                                                const baseUrl = import.meta.env.VITE_API_BASE_URL || '';
                                                const url = `${baseUrl}/jobs/export/excel?accountId=${accountData?._id}`;
                                                
                                                const response = await fetch(url, {
                                                    headers: { 'auth-token': token }
                                                });
                                                
                                                if (!response.ok) throw new Error('Errore durante l\'export');
                                                
                                                const blob = await response.blob();
                                                
                                                clearInterval(progressInterval);
                                                setExportProgress(100);
                                                
                                                // Download del file
                                                const downloadUrl = window.URL.createObjectURL(blob);
                                                const a = document.createElement('a');
                                                a.href = downloadUrl;
                                                a.download = `commesse_export_${new Date().toISOString().slice(0,10)}.xlsx`;
                                                document.body.appendChild(a);
                                                a.click();
                                                a.remove();
                                                window.URL.revokeObjectURL(downloadUrl);
                                                
                                                // Chiudi modale dopo breve delay
                                                setTimeout(() => {
                                                    setShowExportModal(false);
                                                    setIsExporting(false);
                                                    setExportProgress(0);
                                                    toast.success('Export completato!');
                                                }, 500);
                                                
                                            } catch (err) {
                                                clearInterval(progressInterval);
                                                console.error('Errore export:', err);
                                                toast.error('Errore durante l\'export');
                                                setIsExporting(false);
                                                setExportProgress(0);
                                            }
                                        }}
                                        style={{
                                            padding: '10px 20px',
                                            backgroundColor: '#10b981',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            fontWeight: 500
                                        }}
                                    >
                                        <DynamicIcon iconClass="fas fa-download" style={{ marginRight: '8px' }} />
                                        Conferma Export
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            
            {/* Modale di conferma eliminazione */}
            {showDeleteModal && jobToDelete && (
                <div 
                    style={{
                        display: 'block', 
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        width: '100vw',
                        height: '100vh',
                        zIndex: 9999,
                        overflow: 'auto'
                    }} 
                    tabIndex="-1"
                >
                    <div style={{
                        position: 'relative',
                        width: '500px',
                        maxWidth: '90%',
                        margin: '10vh auto',
                        backgroundColor: '#fff',
                        borderRadius: '5px',
                        boxShadow: '0 3px 9px rgba(0,0,0,.5)'
                    }}>
                        <div style={{ padding: '15px', borderBottom: '1px solid #e5e5e5', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h5 style={{ margin: 0, fontWeight: 'bold', color: '#dc3545' }}>
                                <DynamicIcon iconClass="fa-solid fa-exclamation-triangle" style={{ marginRight: '8px' }} />
                                Conferma eliminazione
                            </h5>
                            <button 
                                type="button" 
                                onClick={() => { setShowDeleteModal(false); setJobToDelete(null); }}
                                style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}
                            >
                                &times;
                            </button>
                        </div>
                        <div style={{ padding: '20px' }}>
                            <p>Sei sicuro di voler eliminare questa commessa?</p>
                            <div style={{ marginTop: '10px', backgroundColor: 'rgba(0,0,0,0.03)', padding: '10px', borderRadius: '4px' }}>
                                <p style={{ margin: 0, fontWeight: 'bold' }}>
                                    {jobToDelete.code} - {jobToDelete.customerLabel || 'Cliente N/D'}
                                </p>
                                <p style={{ margin: '5px 0 0 0', fontSize: '0.9rem' }}>
                                    {jobToDelete.jobTypeLabel || 'Tipo N/D'}
                                </p>
                            </div>
                            <p style={{ marginTop: '15px', color: '#dc3545' }}>
                                <strong>Attenzione:</strong> Questa operazione non può essere annullata.
                            </p>
                        </div>
                        <div style={{ padding: '15px', borderTop: '1px solid #e5e5e5', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                            <button 
                                type="button" 
                                onClick={() => { setShowDeleteModal(false); setJobToDelete(null); }}
                                disabled={isDeleting}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#6c757d',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: isDeleting ? 'not-allowed' : 'pointer',
                                    opacity: isDeleting ? 0.65 : 1
                                }}
                            >
                                Annulla
                            </button>
                            <button 
                                type="button" 
                                onClick={async () => {
                                    if (!jobToDelete || !jobToDelete._id) return;
                                    setIsDeleting(true);
                                    try {
                                        const baseUrl = import.meta.env.VITE_API_BASE_URL || '';
                                        const response = await fetch(`${baseUrl}/jobs`, {
                                            method: 'DELETE',
                                            headers: {
                                                'Content-Type': 'application/json',
                                                'auth-token': token
                                            },
                                            body: JSON.stringify({ jobId: jobToDelete._id })
                                        });
                                        if (!response.ok) throw new Error('Errore durante l\'eliminazione');
                                        setShowDeleteModal(false);
                                        setJobToDelete(null);
                                        // Ricarica la lista
                                        dispatch(getJobsList({
                                            accountId: accountData?._id,
                                            token,
                                            search: searchTerm.length >= 2 ? searchTerm : '',
                                            start: 0,
                                            length: 10,
                                            jobStatusId: jobStatusFilter,
                                            jobTypeId: jobTypeFilter,
                                            systemTypeId: systemTypeFilter
                                        }));
                                    } catch (error) {
                                        console.error('Errore eliminazione:', error);
                                        toast.error('Errore durante l\'eliminazione della commessa');
                                    } finally {
                                        setIsDeleting(false);
                                    }
                                }}
                                disabled={isDeleting}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#dc3545',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: isDeleting ? 'not-allowed' : 'pointer',
                                    opacity: isDeleting ? 0.65 : 1
                                }}
                            >
                                {isDeleting ? 'Eliminazione...' : 'Elimina commessa'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
